import { Grid } from "@gcs/design-system/react";
import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { ModuleContext, PageContext } from "../../contexts";
import {
  Section,
  SectionContent,
  Headline,
  Breadcrumb,
  Link,
  Text,
} from "../common";
import BannerStyled, {
  BannerTitle,
  BannerColumnContent,
  CollumnContainer,
  BackIcon,
  BackLink,
  Back,
  SubPageBreadcrumb,
} from "./Banner.Style";

const GRID_MAX_COLLUMNS = 12;
const GRID_FULL_COLUMN = 1;
const GRID_SIZE_MD_COLUMN = 6;
const GRID_SIZE_LG_COLUMN = 6;

const DEFAULT_GRID_WIDTHS = {
  xs: 0,
  sm: 0,
  md: 0,
  lg: GRID_SIZE_MD_COLUMN,
  xl: GRID_SIZE_LG_COLUMN,
};

const checkSizes = ({ xs, sm, md, lg, xl }) => ({
  xs: xs ? xs / GRID_MAX_COLLUMNS : GRID_FULL_COLUMN,
  sm: sm ? sm / GRID_MAX_COLLUMNS : GRID_FULL_COLUMN,
  md: md ? md / GRID_MAX_COLLUMNS : GRID_FULL_COLUMN,
  lg: lg / GRID_MAX_COLLUMNS,
  xl: xl / GRID_MAX_COLLUMNS,
});

const getCrumbs = ({
  module: { moduleName, moduleUrl },
  parentPage,
  pageTitle,
}) => {
  return [
    { label: moduleName, href: moduleUrl },
    parentPage ? { label: parentPage.pageTitle, href: parentPage.path } : null,
    { label: pageTitle },
  ].filter((c) => c);
};

const CrumbsContent = ({ exists, children }) => {
  return exists ? children : null;
};

const VisibleBannerTitle = ({
  styleAs,
  smallerText,
  bannerTitle,
  show,
  bottomText,
  descricao,
}) =>
  show && (
    <BannerTitle as={Headline} nivel="1" styleAs={styleAs}>
      {smallerText && <small>{smallerText}</small>}
      {bannerTitle}
      {descricao && <Text textSize="lg">{descricao}</Text>}
      {bottomText && <Text>{bottomText}</Text>}
    </BannerTitle>
  );

VisibleBannerTitle.defaultProps = {
  smallerText: null,
  bannerTitle: null,
  show: true,
  bottomText: null,
  descricao: null,
};

VisibleBannerTitle.propTypes = {
  styleAs: PropTypes.string.isRequired,
  smallerText: PropTypes.string,
  bannerTitle: PropTypes.string,
  show: PropTypes.bool,
  bottomText: PropTypes.string,
  descricao: PropTypes.string,
};

const Banner = (props) => {
  const {
    smallerText,
    children,
    backgroundImage,
    backgroundImageMobile,
    contentHeight,
    bannerTitle: overrideBanner,
    className: propsClassName,
    contentWidth,
    noBreadCrumbs,
    noTitle,
    secundaryContent,
    reverse,
    bottomText,
    descricao,
  } = props;

  const { moduleName, pages, moduleUrl } = useContext(ModuleContext);

  const { pageTitle, path: currentPath } = useContext(PageContext);

  const subPage = useMemo(
    () => pages.find(({ path }) => currentPath === path),
    [currentPath, pages]
  );

  const parentPage = useMemo(
    () =>
      pages.find(
        ({ path }) =>
          subPage?.parentPage && path === `${moduleUrl}${subPage?.parentPage}`
      ),
    [moduleUrl, pages, subPage?.parentPage]
  );

  const bannerTitle = useMemo(
    () => overrideBanner || (subPage ? pageTitle : moduleName),
    [moduleName, overrideBanner, pageTitle, subPage]
  );

  const className = useMemo(
    () =>
      ["Banner", subPage ? " bottom-hide" : "", propsClassName]
        .filter((x) => x)
        .join(" "),
    [propsClassName, subPage]
  );

  const { xs, sm, md, lg, xl } = useMemo(
    () => ({ ...DEFAULT_GRID_WIDTHS, ...contentWidth }),
    [contentWidth]
  );

  const crumbs = useMemo(
    () =>
      getCrumbs({ module: { moduleName, moduleUrl }, parentPage, pageTitle }),
    [moduleName, moduleUrl, pageTitle, parentPage]
  );

  const contentWidths = useMemo(() => checkSizes({ lg, md, sm, xl, xs }), [
    lg,
    md,
    sm,
    xl,
    xs,
  ]);

  return (
    <BannerStyled
      {...props}
      className={className}
      as={Section}
      backgroundImage={subPage ? null : backgroundImage}
      backgroundImageMobile={subPage ? null : backgroundImageMobile}
      contentHeight={contentHeight}
    >
      <CrumbsContent exists={subPage && !noBreadCrumbs}>
        <Back>
          <BackLink as={Link} href={parentPage?.path || moduleUrl}>
            <BackIcon as={FaChevronLeft} /> Voltar
          </BackLink>
        </Back>
      </CrumbsContent>
      <SectionContent>
        <CrumbsContent exists={subPage && !noBreadCrumbs}>
          <SubPageBreadcrumb as={Breadcrumb} crumbs={crumbs} />
        </CrumbsContent>
        <CollumnContainer as={Grid}>
          {secundaryContent && (
            <BannerColumnContent
              as={Grid.Item}
              xs={1 - contentWidths.xs}
              sm={1 - contentWidths.sm}
              md={1 - contentWidths.md}
              lg={1 - contentWidths.lg}
              xl={1 - contentWidths.xl}
            >
              {secundaryContent}
            </BannerColumnContent>
          )}
          <BannerColumnContent
            reverse={reverse}
            as={Grid.Item}
            {...contentWidths}
          >
            <VisibleBannerTitle
              bannerTitle={bannerTitle}
              smallerText={smallerText}
              show={!noTitle}
              styleAs={subPage ? "3" : "1"}
              bottomText={bottomText}
              descricao={descricao}
            />
            {children}
          </BannerColumnContent>
        </CollumnContainer>
      </SectionContent>
    </BannerStyled>
  );
};

Banner.defaultProps = {
  ...Section.defaultProps,
  smallerText: null,
  bannerTitle: null,
  contentWidth: null,
  noBreadCrumbs: false,
  contentHeight: null,
  noTitle: false,
  reverse: false,
  secundaryContent: null,
  bottomText: null,
  descricao: null,
};

Banner.propTypes = {
  ...Section.propTypes,
  descricao: PropTypes.string,
  bottomText: PropTypes.string,
  smallerText: PropTypes.string,
  bannerTitle: PropTypes.string,
  contentWidth: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
  }),
  noBreadCrumbs: PropTypes.bool,
  contentHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noTitle: PropTypes.bool,
  reverse: PropTypes.bool,
  secundaryContent: PropTypes.element,
};

export default Banner;
